import React, { useState } from 'react';

import {
  Badge,
  Button,
  Checkbox,
  Group,
  LoadingOverlay,
  Modal,
  Paper,
  Stack,
  Text,
  Title,
  Tooltip,
  useMantineTheme,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useShallowEffect } from '@mantine/hooks';
import { IconExclamationCircle } from '@tabler/icons-react';
import { invoicePaymentMethodLabel } from 'components/invoices/InvoiceFilterBar';
import KiloAlert from 'components/KiloAlert';
import MoneyField from 'components/MoneyField';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useInvoice from 'hooks/invoice/useInvoice';
import useRefundInvoice from 'hooks/invoice/useRefundInvoice';

interface FormValues {
  invoiceId: string;
  amount: number;
  refundFullAmount: boolean;
  maxRefundAmount: number;
}

interface InvoiceRefundModalProps {
  invoiceId: string;
  opened: boolean;
  close: () => void;
}

const InvoiceRefundModal = ({ invoiceId, opened, close }: InvoiceRefundModalProps): JSX.Element => {
  const theme = useMantineTheme();
  const { formatCurrency, country, dateFormat } = useLocalization();
  const refundInvoiceMutation = useRefundInvoice();

  const [submissionState, setSubmissionState] = useState<'success' | 'fail' | null>(null);
  const [response, setResponse] = useState<string>('');
  const [refundAmount, setRefundAmount] = useState<number>(0);

  const { data, isLoading, isSuccess } = useInvoice(invoiceId);

  const form = useForm<FormValues>({
    initialValues: {
      invoiceId: invoiceId,
      amount: 0,
      refundFullAmount: false,
      maxRefundAmount: 0,
    },

    validate: {
      amount: (value, formValues) => {
        if (value <= 0) return 'Refund amount must be greater than $0.00';
        if (value > formValues.maxRefundAmount)
          return `Refund amount must be less than or equal to ${formatCurrency(formValues.maxRefundAmount)}`;
        if (formValues.refundFullAmount && value !== formValues.maxRefundAmount)
          return 'Refund amount must match the full amount if "Refund Full Amount" is checked';
        return null;
      },
    },
  });

  const handleRefundFullAmountChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const checked = e.currentTarget.checked;
    if (checked) {
      form.setValues(prevValues => ({
        ...prevValues,
        amount: prevValues.maxRefundAmount,
        refundFullAmount: checked,
      }));
    } else {
      form.setValues(prevValues => ({
        ...prevValues,
        amount: 0,
        refundFullAmount: checked,
      }));
    }
  };

  const handleAmountChange = (value: number): void => {
    if (value === form.values.maxRefundAmount) {
      form.setValues(prevValues => ({
        ...prevValues,
        amount: value,
        refundFullAmount: true,
      }));
    } else {
      form.setValues(prevValues => ({
        ...prevValues,
        amount: value,
        refundFullAmount: false,
      }));
    }
  };

  const handleRefund = (values: FormValues): void => {
    if (data?.data) {
      refundInvoiceMutation.mutate(
        { invoiceId: invoiceId, amount: Math.trunc(values.amount * 100) },
        {
          onError: error => {
            setSubmissionState('fail');
            setResponse(`Refund failed. ${error.response?.data.message}`);
          },
          onSuccess: res => {
            setSubmissionState('success');
            setRefundAmount(values.amount);
            if (res.data.status === 'REFUND PENDING') {
              setResponse(`Refund is in progress, no further action needed.`);
            } else {
              setResponse(`The refund has succeeded!`);
            }
          },
        },
      );
    }
  };

  const handleClose = (): void => {
    close();
    setSubmissionState(null);
    setRefundAmount(0);
    setResponse('');
    if (isSuccess && data?.data) {
      const max = (data.data.total - data.data.amountRefunded) / 100;
      form.setValues({
        amount: max,
        refundFullAmount: true,
        maxRefundAmount: max,
      });
    } else {
      form.reset();
    }
  };

  useShallowEffect(() => {
    if (isSuccess && data?.data) {
      const max = (data.data.total - data.data.amountRefunded) / 100;
      form.setValues({
        amount: max,
        refundFullAmount: true,
        maxRefundAmount: max,
      });
    }
  }, [data?.data, isSuccess]);

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      trapFocus
      fullScreen
      size='lg'
      transitionProps={{ transition: 'fade', duration: 200 }}
      styles={{
        content: { display: 'flex', flexDirection: 'column' },
        body: { display: 'flex', flexGrow: 1, flexDirection: 'column' },
      }}
    >
      <LoadingOverlay visible={isLoading} overlayBlur={2} />
      <div
        style={{
          display: 'grid',
          gridTemplateRows: 'min-content auto min-content',
          gridTemplateColumns: 'auto',
          gap: '2rem',
          flexGrow: 1,
        }}
      >
        <Title order={1} color='gray.8' ta={'center'}>
          Refund Payment
        </Title>
        <div style={{ flexGrow: 1 }}>
          {isSuccess && data?.data && (
            <form id='refund-form' onSubmit={form.onSubmit(handleRefund)}>
              <Stack maw={550} m='auto'>
                {submissionState === 'success' && (
                  <KiloAlert
                    variantStyle='success'
                    title={`Refund for ${formatCurrency(refundAmount)} confirmed!`}
                  >
                    {response}
                  </KiloAlert>
                )}
                <Paper
                  bg='gray.1'
                  p='md'
                  style={{
                    display: 'grid',
                    rowGap: '1rem',
                    columnGap: '1.5rem',
                    gridTemplateColumns: 'fit-content(180px) auto',
                  }}
                >
                  <Text fw={500} fs='italic' color='gray.6'>
                    Athlete Name
                  </Text>
                  <Text fw={500}>{data.data.athlete.fullName || 'N/A'}</Text>
                  <Text fw={500} fs='italic' color='gray.6'>
                    Invoice Total
                  </Text>
                  <Text fw={500}>{formatCurrency(data.data.total / 100)}</Text>
                  {data.data.invoiceItems.map(invoiceItem => {
                    const text =
                      invoiceItem.quantity === 1
                        ? invoiceItem.productName
                        : `(${invoiceItem.quantity}) ${invoiceItem.productName}`;
                    return (
                      <React.Fragment key={invoiceItem.invoiceItemId}>
                        <Group spacing='xs' ml='md' noWrap>
                          <Tooltip label={text}>
                            <Text color='gray.6' truncate>
                              {text}
                            </Text>
                          </Tooltip>
                          {invoiceItem.discount && (
                            <Badge color='blue' variant='light' size='sm'>
                              discounted
                            </Badge>
                          )}
                        </Group>
                        <Text color='gray.6'>{formatCurrency(invoiceItem.totalPrice / 100)}</Text>
                      </React.Fragment>
                    );
                  })}
                  <Text fw={500} fs='italic' color='gray.6'>
                    Paid Amount
                  </Text>
                  <Text fw={500}>{formatCurrency(form.values.maxRefundAmount)}</Text>
                  <Text fw={500} fs='italic' color='gray.6'>
                    Payment Date
                  </Text>
                  <Text fw={500}>{dayjs(data.data.paymentDate).format(dateFormat)}</Text>
                  <Text fw={500} fs='italic' color='gray.6'>
                    Payment Method
                  </Text>
                  <Stack spacing='xs'>
                    <Text fw={500}>{invoicePaymentMethodLabel(data.data)}</Text>
                    {data.data.isTapToPay && (
                      <Group spacing='xs' align='center'>
                        <IconExclamationCircle size='1.2rem' color={theme.colors.gray[6]} />
                        <Text size='xs' color='dimmed'>
                          This invoice was collected via Tap to Pay
                        </Text>
                      </Group>
                    )}
                  </Stack>
                </Paper>
                {submissionState !== 'success' && (
                  <>
                    <MoneyField
                      country={country}
                      label='Amount to Refund'
                      placeholder='Enter refund amount'
                      required
                      min={0}
                      max={form.values.maxRefundAmount}
                      value={form.values.amount}
                      onChange={handleAmountChange}
                      error={form.errors.amount}
                    />
                    <Checkbox
                      label='Refund Full Amount'
                      checked={form.values.refundFullAmount}
                      onChange={handleRefundFullAmountChange}
                    />
                    {!form.values.refundFullAmount && (
                      <KiloAlert variantStyle='warning'>
                        A partial refund will not reopen the invoice. If you want to reopen the invoice,
                        please refund the full paid amount.
                      </KiloAlert>
                    )}
                    {data.data.invoiceItems.some(i => i.discount) && (
                      <KiloAlert variantStyle='default'>
                        One or more items had a discount applied. The amount shown already reflects the final
                        price paid.
                      </KiloAlert>
                    )}
                    <Text fw={500} py='sm' ta='center'>
                      Are you sure you want to refund this payment?
                    </Text>
                  </>
                )}
                {submissionState === 'fail' && <KiloAlert variantStyle='error'>{response}</KiloAlert>}
              </Stack>
            </form>
          )}
        </div>
        <Group position='right' pt='md'>
          {submissionState === 'success' ? (
            <Button variant='outline' onClick={handleClose}>
              Close
            </Button>
          ) : (
            <>
              <Button variant='outline' onClick={handleClose}>
                Cancel
              </Button>
              <Button
                form='refund-form'
                type='submit'
                disabled={isLoading}
                loading={refundInvoiceMutation.isLoading}
              >
                Confirm Refund
              </Button>
            </>
          )}
        </Group>
      </div>
    </Modal>
  );
};

export default InvoiceRefundModal;
